<script setup lang="ts">
import { ChevronDown, X } from 'lucide'
import { SelectIcon, SelectTrigger, useForwardProps } from 'reka-ui'
import type { HTMLAttributes } from 'vue'
import type { SelectTriggerProps } from 'reka-ui'
import { SphIcon } from '@/components/base/icon/'
import { cn } from '@/utils/shadcn/lib/utils.ts'
import { reactiveOmit } from '@vueuse/core'
import { useI18n } from '@/composables/i18n';

defineOptions({
    compatConfig: {
        MODE: 3,
    },
    inheritAttrs: false,
})

const { i18n } = useI18n()

const props = withDefaults(defineProps<SelectTriggerProps & {
    class?: HTMLAttributes['class'],
    id?: string,
    size?: 'sm' | 'default',
    hasClearButton?: boolean,
    showClearButton?: boolean,
    placeholder?: string,
}>(), {
    class: '',
    id: undefined,
    size: 'default',
    hasClearButton: false,
    showClearButton: false,
    placeholder: undefined,
})

const emit = defineEmits<{
    clear: []
}>()

const delegatedProps = reactiveOmit(props, 'class', 'size')
const forwardedProps = useForwardProps(delegatedProps)
</script>

<template>
    <!-- w-fit keeps the trigger at content width, max-w-full stops it from growing past whatever
         width the wrapping .sph-select was given (e.g. class="w-56") -->
    <div class="relative w-fit max-w-full">
        <SelectTrigger
            v-bind="{ ...forwardedProps, ...$attrs }"
            data-slot="select-trigger"
            :data-size="size"
            class="sph-form-control"
            :class="cn(
                'bg-input border-input-dark text-input-foreground data-placeholder:text-muted-foreground font-normal [&_svg:not([class*=\'text-\'])]:text-muted-foreground  w-full flex items-center justify-between gap-2 min-w-0 overflow-hidden rounded-md border px-3 py-2 text-base whitespace-nowrap shadow-xs transition-[color,box-shadow] outline-none data-[size=default]:h-(--input-height) data-[size=sm]:h-8 *:data-[slot=select-value]:line-clamp-1',
                props.class,
            )"
        >
            <div
                class="content"
                :data-placeholder-text="props.placeholder"
                :class="hasClearButton ? 'mr-5' : ''"
            >
                <slot></slot>
            </div>
            <SelectIcon as-child>
                <SphIcon
                    :icon="ChevronDown"
                    size="sm"
                    class="flex-none opacity-50"
                />
            </SelectIcon>
        </SelectTrigger>
        <div
            v-if="hasClearButton"
            :aria-label="i18n.t('Auswahl löschen')"
            tabindex="0"
            role="button"
            class="clear-button"
            :class="{ 'show': showClearButton }"
            @click.prevent.stop="emit('clear')"
            @keydown.enter.prevent.stop="emit('clear')"
            @keydown.space.prevent.stop="emit('clear')"
        >
            <SphIcon
                :icon="X"
                size="sm"
            />
        </div>
    </div>
</template>

<style scoped>
@reference '@/assets/styles/config.css';

[data-slot=select-trigger] {
    > .content {
        display: block;
        /* without this the flex item's automatic minimum size is the nowrap text width, so it
           never shrinks and the trigger grows past the width it was given */
        flex: 1;
        min-width: 0;
        overflow: hidden;
        text-overflow: ellipsis;
        white-space: nowrap;

        &::before {
            content: attr(data-placeholder-text);
            display: block;
            height: 0;
            overflow: hidden;
            visibility: hidden;
            white-space: nowrap;
        }
    }
}
</style>

<style scoped>
@reference '@/assets/styles/config.css';

.clear-button {
    @apply absolute right-8 top-0 z-10 size-7 h-full flex items-center justify-center rounded-sm cursor-pointer text-muted-foreground hover:text-accent transition-all border border-transparent focus-visible:border-ring/50 outline-none select-none pointer-events-none opacity-0;

    &.show {
        @apply opacity-100 pointer-events-auto;
    }
}
</style>
